import * as THREE from 'three'

export type Plane = 'x' | 'y' | 'z'

type PointIndex = 'a' | 'b' | 'c'

interface PlanarMapItem {
  xPlane: Plane
  yPlane: Plane
}

type PlanarMap = Record<Plane, PlanarMapItem>

interface Point {
  x: number
  y: number
}

interface Size {
  width: number
  height: number
}

const planarMap: PlanarMap = {
  x: {
    xPlane: 'z',
    yPlane: 'y',
  },
  y: {
    xPlane: 'x',
    yPlane: 'z',
  },
  z: {
    xPlane: 'x',
    yPlane: 'y',
  },
}

/**
 * Converts a UV index ('a', 'b' or 'c') into a numerical index (0, 1, or 2).
 */
const uvIndexToIndex = (uvIndex: PointIndex) =>
  uvIndex.charCodeAt(0) - 'a'.charCodeAt(0)

export class PlanarUVBuilder {
  private geometry: THREE.Geometry
  private vertices: [THREE.Vector3, THREE.Vector3, THREE.Vector3]
  private uvs: [
    THREE.Vector2 | null,
    THREE.Vector2 | null,
    THREE.Vector2 | null,
  ] = [null, null, null]
  private uvBoundingBox: THREE.Box2 | null = null
  private scalingFactor: number
  private xPlane: Plane
  private yPlane: Plane

  /**
   * Initializes planar UV builder:
   * - Sets up the x and y axes based on plane.
   * - Generates the initial UV coordinates.
   * - Calculates the bounding box of the UVs.
   */
  constructor(
    geometry: THREE.Geometry,
    plane: Plane,
    vertexIndices: [number, number, number],
    scalingFactor: number = 1,
  ) {
    this.geometry = geometry
    this.vertices = [
      this.geometry.vertices[vertexIndices[0]],
      this.geometry.vertices[vertexIndices[1]],
      this.geometry.vertices[vertexIndices[2]],
    ]
    this.scalingFactor = scalingFactor

    const planarMapItem = planarMap[plane]
    this.xPlane = planarMapItem.xPlane
    this.yPlane = planarMapItem.yPlane

    this.generateUVs()
    this.regenerateUvBoundingBox()
  }

  /**
   * Returns the current UV coordinates.
   */
  private getUvs() {
    if (this.uvs[0] === null || this.uvs[1] === null || this.uvs[2] === null) {
      throw new Error('Failed to initialize planar UV builder UVs.')
    }

    return this.uvs as [THREE.Vector2, THREE.Vector2, THREE.Vector2]
  }

  /**
   * Calculates the initial UV coordinates based on the geometry and plane settings.
   */
  private generateUVs() {
    this.uvs = [
      new THREE.Vector2(
        this.vertices[0][this.xPlane],
        this.vertices[0][this.yPlane],
      ),
      new THREE.Vector2(
        this.vertices[1][this.xPlane],
        this.vertices[1][this.yPlane],
      ),
      new THREE.Vector2(
        this.vertices[2][this.xPlane],
        this.vertices[2][this.yPlane],
      ),
    ]
  }

  /**
   * Resets the UV coordinates to (0,0).
   */
  public reset(excludeIndices: number[] = []) {
    return this.transform((uv) => {
      uv.setX(0)
      uv.setY(0)
    }, excludeIndices)
  }

  /**
   * Moves all UV coordinates by moving anchor point to specified coordinates.
   */
  public move(anchorUvIndex: PointIndex, x: number | null, y: number | null) {
    const uvs = this.getUvs()

    const anchorIndex = uvIndexToIndex(anchorUvIndex)
    const anchorUv = uvs[anchorIndex]

    if (anchorUv === null) {
      throw new Error('Failed to initialize planar UV builder anchor point.')
    }

    const anchorOriginal: Point = {
      x: anchorUv.x,
      y: anchorUv.y,
    }
    const anchorDifferenceX = x !== null ? x - anchorOriginal.x : null
    const anchorDifferenceY = y !== null ? y - anchorOriginal.y : null

    if (x !== null) {
      anchorUv.setX(x)
    }

    if (y !== null) {
      anchorUv.setY(y)
    }

    return this.transform((uv, index) => {
      if (index === anchorIndex) {
        return
      }

      if (x !== null && anchorDifferenceX !== null) {
        uv.setX(uv.x + anchorDifferenceX)
      }

      if (y !== null && anchorDifferenceY !== null) {
        uv.setY(uv.y + anchorDifferenceY)
      }
    })
  }

  /**
   * Gets the coordinates of the pivot point for rotation.
   */
  private getPivotCoordinates(pivot: PointIndex | THREE.Vector2) {
    if (pivot instanceof THREE.Vector2) {
      return [pivot.x, pivot.y] as const
    }

    const uvs = this.getUvs()
    const pivotIndex = uvIndexToIndex(pivot)
    const pivotUv = uvs[pivotIndex]

    if (pivotUv === null) {
      throw new Error('Failed to initialize planar UV builder pivot point.')
    }

    return [pivotUv.x, pivotUv.y] as const
  }

  /**
   * Mirrors the UV coordinates around X axis.
   */
  public mirrorX(x: number) {
    return this.transform((uv) => {
      uv.setX(2 * x - uv.x)
    })
  }

  /**
   * Mirrors the UV coordinates around Y axis.
   */
  public mirrorY(y: number) {
    return this.transform((uv) => {
      uv.setY(2 * y - uv.y)
    })
  }

  /**
   * Rotates the UV coordinates around a pivot point.
   */
  public rotate(angle: number, pivot: PointIndex | THREE.Vector2) {
    const angleSine = Math.sin(angle)
    const angleCosine = Math.cos(angle)

    const [pivotPointX, pivotPointY] = this.getPivotCoordinates(pivot)

    return this.transform((uv) => {
      const x = uv.x
      const y = uv.y

      // Check if point is rotation origin
      if (x == pivotPointX && y == pivotPointY) {
        return true
      }

      // Translate point to rotation origin
      const translatedX = x - pivotPointX
      const translatedY = y - pivotPointY

      // Rotate
      const rotatedX = translatedX * angleCosine - translatedY * angleSine
      const rotatedY = translatedX * angleSine + translatedY * angleCosine

      // Translate point back
      uv.setX(rotatedX + pivotPointX)
      uv.setY(rotatedY + pivotPointY)
    })
  }

  /**
   * Offsets the UV coordinates by a specified amount.
   */
  public offset(offsetX: number, offsetY: number) {
    return this.transform((uv) => {
      uv.setX(uv.x + offsetX)
      uv.setY(uv.y + offsetY)
    })
  }

  /**
   * Applies a custom transformation function to the UV coordinates.
   */
  public transform(
    transformer: (
      uv: THREE.Vector2,
      index: number,
      allPoints: THREE.Vector2[],
    ) => void,
    excludeIndices: number[] = [],
  ) {
    const uvs = this.getUvs()

    uvs
      .filter((_, index) => !excludeIndices.includes(index))
      .forEach(transformer)

    this.regenerateUvBoundingBox()

    return this
  }

  /**
   * Returns current UV coordinates with scaling factor.
   */
  public toUv() {
    if (this.uvs[0] === null || this.uvs[1] === null || this.uvs[2] === null) {
      throw new Error('Failed to initialize planar UV builder UVs.')
    }

    return [
      new THREE.Vector2(
        this.uvs[0].x * this.scalingFactor,
        this.uvs[0].y * this.scalingFactor,
      ),
      new THREE.Vector2(
        this.uvs[1].x * this.scalingFactor,
        this.uvs[1].y * this.scalingFactor,
      ),
      new THREE.Vector2(
        this.uvs[2].x * this.scalingFactor,
        this.uvs[2].y * this.scalingFactor,
      ),
    ]
  }

  /**
   * Returns a copy of the UV bounding box.
   */
  public getUvBoundingBox() {
    if (this.uvBoundingBox === null) {
      throw new Error('Failed to initialize planar UV builder bounding box.')
    }

    return this.uvBoundingBox.clone()
  }

  /**
   * Returns the size of the UV bounding box.
   */
  public getUvBoundingBoxSize() {
    const uvBoundingBox = this.getUvBoundingBox()
    const size: Size = {
      width: uvBoundingBox.max.x - uvBoundingBox.min.x,
      height: uvBoundingBox.max.y - uvBoundingBox.min.y,
    }

    return size
  }

  /**
   * Allows you to "tap" into the builder and perform custom actions.
   */
  public tap(tapper: (builder: PlanarUVBuilder) => void) {
    tapper(this)
    return this
  }

  /**
   * Recalculates the UV bounding box.
   */
  private regenerateUvBoundingBox() {
    const uvs = this.getUvs()

    this.uvBoundingBox = new THREE.Box2()
    this.uvBoundingBox.setFromPoints(uvs)
  }
}
